'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { assetPrefix } from '@ahm/api-wrappers-core';
import React, { useCallback, useEffect, useRef, useState } from 'react';

interface HighlightItemsProps {
  items: any[];
}

const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('vi-VN').format(price);
};

const HighlightItems: React.FC<HighlightItemsProps> = ({ items }) => {
  // Auto-scroll functionality for desktop
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const currentIndexRef = useRef(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoScroll = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      currentIndexRef.current = (currentIndexRef.current + 1) % items.length;

      if (scrollContainerRef.current) {
        const itemWidth = 280 + 16; // item width + gap
        scrollContainerRef.current.scrollTo({
          left: currentIndexRef.current * itemWidth,
          behavior: 'smooth',
        });
      }
    }, 2000); // Auto-scroll every 2 seconds
  }, [items.length]);

  const stopAutoScroll = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  useEffect(() => {
    if (isHovered && items.length > 1) {
      startAutoScroll();
    } else {
      stopAutoScroll();
    }

    return () => stopAutoScroll();
  }, [isHovered, items.length, startAutoScroll]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  if (!items || items.length === 0) return null;

  // Chỉ lấy 2 item đầu tiên trên mobile
  const displayItems = items.slice(0, 2);

  return (
    <div className="mt-3 w-full">
      {/* Mobile: hiển thị theo cột */}
      <div className="flex flex-col gap-2 md:hidden">
        {displayItems.map((item) => (
          <div
            key={item._id}
            className="flex items-center gap-3 rounded-lg bg-neutral-5 p-3"
          >
            <div className="relative size-12 flex-none overflow-hidden rounded-lg bg-neutral-15">
              <Avatar className="rounded-lg object-cover size-full">
                <AvatarImage
                  src={
                    item.image_preview_url && item.image_preview_url !== ''
                      ? item.image_preview_url
                      : item.image_thumb_url ||
                        `${assetPrefix}/static/images/empty-thumbnail.webp`
                  }
                  alt={item.name}
                  className="size-full"
                  style={{ objectFit: 'cover' }}
                />
                <AvatarFallback className="size-full">
                  {item.name ? item.name.charAt(0) : 'F'}
                </AvatarFallback>
              </Avatar>
            </div>
            <div className="flex-1 w-full">
              <h4 className="line-clamp-2 text-sm font-medium text-neutral-60">
                {item.name}
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-neutral-90">
                  đ{formatPrice(item.price)}
                </span>
                {item.original_price && item.original_price > item.price && (
                  <span className="text-xs text-neutral-50 line-through">
                    đ{formatPrice(item.original_price)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Desktop: hiển thị theo hàng ngang */}
      {/* <div className="hidden gap-3 overflow-x-auto pb-2 scrollbar-hide md:flex">
        {items.map((item) => (
          <div
            key={item._id}
            className="flex min-w-[200px] flex-none items-center gap-3 rounded-lg bg-neutral-5 py-3"
          >
            <div className="relative size-14 flex-none overflow-hidden rounded-lg bg-neutral-15">
              <ImageWithFallback
                src={
                  item.image_thumb_url ||
                  `${assetPrefix}/static/images/empty-thumbnail.webp`
                }
                alt={item.name}
                fill
                className="rounded-lg object-cover"
                fallbackSrc={`${assetPrefix}/static/images/empty-food.webp`}
              />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="truncate text-sm font-medium text-neutral-60">
                {item.name}
              </h4>
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-neutral-90">
                  đ{formatPrice(item.price)}
                </span>
                {item.original_price && item.original_price > item.price && (
                  <span className="text-xs text-neutral-50 line-through">
                    đ{formatPrice(item.original_price)}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div> */}
      <div className="hidden md:block">
        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto pb-3 scrollbar-thin scrollbar-track-neutral-10 scrollbar-thumb-neutral-30"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {items.map((item) => (
            <div
              key={item._id}
              className="flex min-w-[280px] max-w-[280px] flex-none cursor-pointer rounded-xl bg-neutral-10/80 pr-3 transition-all duration-200 hover:bg-neutral-10 hover:shadow-md p-0"
            >
              <div className="flex w-full items-center gap-4">
                <div className="relative size-20 flex-none overflow-hidden rounded-xl bg-neutral-15">
                  <Avatar className="rounded-xl object-cover size-full">
                    <AvatarImage
                      src={
                        item.image_thumb_url ||
                        `${assetPrefix}/static/images/empty-thumbnail.webp`
                      }
                      alt={item.name}
                      className="size-full"
                      style={{ objectFit: 'cover' }}
                    />
                    <AvatarFallback className="size-full">
                      {item.name ? item.name.charAt(0) : 'F'}
                    </AvatarFallback>
                  </Avatar>
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="line-clamp-1 text-sm font-semibold text-neutral-90 mb-2">
                    {item.name}
                  </h4>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-neutral-90">
                      đ{formatPrice(item.price)}
                    </span>
                    {item.score && (
                      <div className="flex items-center gap-1 rounded-full bg-yellow-10 px-2 py-1">
                        <span className="text-xs text-yellow-60">★</span>
                        <span className="text-xs font-medium text-yellow-60">
                          {item.score.toFixed(1)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Scroll hint for desktop */}
        {/* {items.length > 3 && (
          <div className="mt-2 text-center">
            <span className="text-xs text-neutral-50">
              ← Vuốt để xem thêm món →
            </span>
          </div>
        )} */}
      </div>
    </div>
  );
};

export default HighlightItems;
